import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SourceTextDialog } from "./SourceTextDialog";
import { MarkSchemeViewDialog } from "./MarkSchemeViewDialog";
import { Delete, Edit, MoreVert } from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";
import { useApiClient } from "../../../api";

export const SourceImagesDialog = ({
  open,
  onClose,
  images,
  selectedIndex,
  onSelectImage,
  onDeleted,
}) => {
  const ApiClient = useApiClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());

  if (!images || images.length === 0) return null;

  const selectedImage = images[selectedIndex];

  const toggleEditMode = () => {
    setEditMode(!editMode);
    setSelectedForDeletion(new Set());
  };

  const toggleImageSelection = (imageId) => {
    const newSelection = new Set(selectedForDeletion);
    if (newSelection.has(imageId)) {
      newSelection.delete(imageId);
    } else {
      newSelection.add(imageId);
    }
    setSelectedForDeletion(newSelection);
  };

  const handleDelete = async () => {
    if (selectedForDeletion.size === 0) return;
    setIsDeleting(true);
    try {
      await ApiClient.question.deleteSourceImage({
        sourceImages: Array.from(selectedForDeletion),
      });

      setEditMode(false);
      setSelectedForDeletion(new Set());

      const remainingCount = images.length - selectedForDeletion.size;
      if (remainingCount > 0) {
        const nextIndex =
          selectedIndex >= remainingCount ? remainingCount - 1 : selectedIndex;
        onSelectImage(nextIndex);
      }

      if (onDeleted) onDeleted();
    } catch (e) {
      console.error("Failed to delete source images", e);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Source Images</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mb: 2,
            textAlign: "center",
            position: "relative",
            display: "inline-block",
            width: "100%",
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              transition: "opacity 0.2s",
              opacity:
                editMode && selectedForDeletion.has(selectedImage.id) ? 0.4 : 1,
            }}
          >
            <img
              src={selectedImage.fileUrl}
              alt={
                selectedImage.description || `Source image ${selectedIndex + 1}`
              }
              style={{
                width: "100%",
                maxHeight: "60vh",
                objectFit: "contain",
              }}
            />
            {editMode && images.length === 1 && (
              <IconButton
                onClick={() => toggleImageSelection(selectedImage.id)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: selectedForDeletion.has(selectedImage.id)
                    ? "error.main"
                    : "background.paper",
                  color: selectedForDeletion.has(selectedImage.id)
                    ? "error.contrastText"
                    : "error.main",
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                  },
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
          {selectedImage.description && (
            <Box sx={{ mt: 2, textAlign: "left" }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Description:</strong> {selectedImage.description}
              </Typography>
            </Box>
          )}
        </Box>

        {images.length > 1 && (
          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1 }}>
            {images.map((img, idx) => (
              <Box
                key={img.id}
                onClick={() => !editMode && onSelectImage(idx)}
                sx={{
                  cursor: editMode ? "default" : "pointer",
                  border: (theme) =>
                    idx === selectedIndex && !editMode
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 0.5,
                  flexShrink: 0,
                  transition: "all 0.2s",
                  position: "relative",
                  opacity:
                    editMode && selectedForDeletion.has(img.id) ? 0.4 : 1,
                  "&:hover": {
                    border: (theme) =>
                      !editMode && `2px solid ${theme.palette.primary.light}`,
                  },
                }}
              >
                <img
                  src={img.fileUrl}
                  alt={`Thumbnail ${idx + 1}`}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    display: "block",
                    borderRadius: 4,
                  }}
                />
                {editMode && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleImageSelection(img.id);
                    }}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      backgroundColor: selectedForDeletion.has(img.id)
                        ? "error.main"
                        : "background.paper",
                      color: selectedForDeletion.has(img.id)
                        ? "error.contrastText"
                        : "error.main",
                      "&:hover": {
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {editMode ? (
          <>
            <Button
              color="error"
              onClick={handleDelete}
              disabled={isDeleting || selectedForDeletion.size === 0}
              startIcon={<Delete />}
            >
              {isDeleting
                ? "Deleting..."
                : `Delete image${selectedForDeletion.size !== 1 ? "s" : ""} (${selectedForDeletion.size})`}
            </Button>
            <Button onClick={toggleEditMode}>Cancel</Button>
          </>
        ) : (
          <>
            <Button onClick={toggleEditMode} startIcon={<Edit />}>
              Edit
            </Button>
            <Button onClick={onClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const QuestionOptionsViewer = ({
  options = [],
  showCorrectAnswer = false,
  showMarks = false,
  quizSubmission = false,
  chosenOptions = [],
  onChosenOptionsChange = null,
}) => {
  if (!options.length) return null;

  const correctCount = options.filter((o) => o.isCorrect).length;
  const isMultiple = correctCount > 1;

  const handleToggleOption = (optionId) => {
    let nextSelectedOptions = [];
    if (isMultiple) {
      const alreadySelected = chosenOptions.includes(optionId);

      if (alreadySelected) {
        nextSelectedOptions = chosenOptions.filter((id) => id !== optionId);
      } else if (chosenOptions.length < correctCount) {
        nextSelectedOptions = [...chosenOptions, optionId];
      } else {
        // max reached → do nothing
        return;
      }
    } else {
      nextSelectedOptions = [optionId];
    }
    onChosenOptionsChange?.(nextSelectedOptions);
  };
  const optionsFont = quizSubmission ? "body1" : "body2";

  const getOptionState = (option) => {
    const isSelected = chosenOptions.includes(option.id);
    const isCorrect = (showMarks || showCorrectAnswer) && option.isCorrect;

    return {
      isSelected,
      isCorrect,
      isCorrectAndSelected: showMarks && isCorrect && isSelected,
      isIncorrectButSelected: showMarks && !isCorrect && isSelected,
    };
  };

  const OptionLabel = ({ option, index }) => {
    const { isCorrect, isIncorrectButSelected } = getOptionState(option);

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant={optionsFont} fontWeight="medium">
          {String.fromCharCode(65 + index)}.
        </Typography>

        <Typography
          variant={optionsFont}
          sx={{
            fontWeight: isCorrect ? 600 : 400,
            color: isCorrect
              ? "success.main"
              : isIncorrectButSelected
                ? "error.main"
                : "text.primary",
          }}
        >
          {option.text}
        </Typography>
      </Stack>
    );
  };

  const OptionMarks = ({ option }) => {
    const { isCorrectAndSelected, isIncorrectButSelected } =
      getOptionState(option);

    if (showMarks) {
      if (isCorrectAndSelected) {
        return (
          <Typography variant="caption" sx={{ color: "success.main" }}>
            ✓ Correct
          </Typography>
        );
      }

      if (isIncorrectButSelected) {
        return (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            ✕ Incorrect
          </Typography>
        );
      }

      return null;
    }

    if (option.isCorrect && showCorrectAnswer) {
      return (
        <Typography variant="caption" sx={{ color: "success.main" }}>
          ✓ Correct
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box sx={{ mt: 1 }}>
      {quizSubmission && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          Select {correctCount} option{correctCount > 1 ? "s" : ""}
        </Typography>
      )}

      {isMultiple ? (
        <Stack spacing={1}>
          {options.map((option, index) => {
            const { isSelected } = getOptionState(option);

            return (
              <Stack
                key={option.id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleOption(option.id)}
                      disabled={
                        !isSelected && chosenOptions.length >= correctCount
                      }
                    />
                  }
                  label={<OptionLabel option={option} index={index} />}
                />

                <OptionMarks option={option} />
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <RadioGroup
          value={chosenOptions[0] || ""}
          onChange={(e) => handleToggleOption(e.target.value)}
        >
          {options.map((option, index) => {
            return (
              <Stack
                key={option.id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <FormControlLabel
                  value={option.id}
                  control={<Radio />}
                  label={<OptionLabel option={option} index={index} />}
                />

                <OptionMarks option={option} />
              </Stack>
            );
          })}
        </RadioGroup>
      )}
    </Box>
  );
};

export const QuestionViewCard = ({
  question,
  onImagesChanged,
  identifier = null,
  hideMarkScheme = false,
  hideOptions = false,
  collapsibleMenu = false,
  sx = {},
  onChosenOptionsChange = null,
  chosenOptions = [],
  quizSubmission = false,
  showMarks = false,
}) => {
  if (quizSubmission && !onChosenOptionsChange) {
    throw Error(
      "For quizSubmission an onChosenOptionsChange function is required",
    );
  } // TODO: add test for this
  const [sourceTextDialogOpen, setSourceTextDialogOpen] = useState(false);
  const [markSchemeDialogOpen, setMarkSchemeDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [sourceImagesDialogOpen, setSourceImagesDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const theme = useTheme();
  const openMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Card
      sx={{
        backgroundColor:
          collapsibleMenu && (theme.vars || theme).palette.background.default,
        mb: 2,
        ...sx,
      }}
    >
      <SourceTextDialog
        open={sourceTextDialogOpen}
        setOpen={setSourceTextDialogOpen}
        sourceText={question?.sourceText}
      />
      <MarkSchemeViewDialog
        open={markSchemeDialogOpen}
        setOpen={setMarkSchemeDialogOpen}
        question={question}
      />
      <SourceImagesDialog
        open={sourceImagesDialogOpen}
        onClose={() => setSourceImagesDialogOpen(false)}
        images={question?.sourceImages || []}
        selectedIndex={selectedImageIndex}
        onSelectImage={setSelectedImageIndex}
        onDeleted={onImagesChanged}
      />
      <CardContent
        sx={{
          display: "flex",
          flexDirection: collapsibleMenu ? "row" : "column",
          justifyContent: collapsibleMenu ? "space-between" : "flex-start",
          alignItems: collapsibleMenu ? "center" : "stretch",
          gap: 2,
        }}
      >
        <Box>
          {identifier && (
            <Typography variant="subtitle1" fontWeight="medium">
              Question {identifier}
            </Typography>
          )}
          <Typography variant="subtitle1">{question.questionText}</Typography>
          {!hideOptions && (
            <QuestionOptionsViewer
              options={question.options}
              chosenOptions={chosenOptions}
              onChosenOptionsChange={onChosenOptionsChange}
              showMarks={showMarks}
              quizSubmission
            />
          )}
        </Box>
        {collapsibleMenu ? (
          <Box>
            <IconButton onClick={openMenu} sx={{ border: "none" }}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={closeMenu}
            >
              {!hideMarkScheme && (
                <MenuItem
                  onClick={() => {
                    setMarkSchemeDialogOpen(true);
                    closeMenu();
                  }}
                >
                  Mark Scheme
                </MenuItem>
              )}
              {question.sourceText && (
                <MenuItem
                  onClick={() => {
                    setSourceTextDialogOpen(true);
                    closeMenu();
                  }}
                >
                  Source Extract
                </MenuItem>
              )}
              {question.sourceImages && question.sourceImages.length > 0 && (
                <MenuItem
                  onClick={() => {
                    setSelectedImageIndex(0);
                    setSourceImagesDialogOpen(true);
                    closeMenu();
                  }}
                >
                  View Source Images ({question.sourceImages.length})
                </MenuItem>
              )}
            </Menu>
          </Box>
        ) : (
          <Stack direction={"row"} spacing={2}>
            {!hideMarkScheme && (
              <Tooltip
                title={
                  !question.markScheme
                    ? "This question does not have a mark scheme"
                    : ""
                }
              >
                <span>
                  <Button
                    disabled={!question.markScheme}
                    variant="outlined"
                    onClick={() => setMarkSchemeDialogOpen(true)}
                  >
                    Mark Scheme
                  </Button>
                </span>
              </Tooltip>
            )}
            {question.sourceText && (
              <Button
                variant="outlined"
                onClick={() => setSourceTextDialogOpen(true)}
              >
                View Source Extract
              </Button>
            )}
            {question.sourceImages && question.sourceImages.length > 0 && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedImageIndex(0);
                  setSourceImagesDialogOpen(true);
                }}
              >
                View Source Images ({question.sourceImages.length})
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
